// src/components/Admin/AdminNavigation.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminNavigation = () => {
  const navigate = useNavigate();
  
  const [stats, setStats] = useState({
    pendingDiscover: 0,
    approvedDiscover: 0,
    totalProducts: 0,
    pendingEditorials: 0,
    activeSignals: 0,
    pendingUserContent: 0,
    totalRestaurants: 0,
    restaurantsWithLiveData: 0,
    // NEW CONTRIBUTOR STATS
    pendingContributors: 0,
    pendingContributorProducts: 0,
    pendingContributorArticles: 0,
    pendingContributorSignals: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);

  // Load overview statistics
  useEffect(() => {
    const loadStats = async () => {
      setIsLoading(true);
      try {
        // Load discover stats
        const discoverPendingResponse = await fetch('/discover-pending.json');
        const discoverApprovedResponse = await fetch('/discover.json');
        
        let pendingDiscover = 0;
        let approvedDiscover = 0;
        
        if (discoverPendingResponse.ok) {
          const pendingData = await discoverPendingResponse.json();
          pendingDiscover = pendingData.entries?.length || 0;
        }
        
        if (discoverApprovedResponse.ok) {
          const approvedData = await discoverApprovedResponse.json();
          approvedDiscover = approvedData.entries?.length || 0;
        }

        // Mock stats for other areas (would be real API calls)
        setStats({
          pendingDiscover,
          approvedDiscover,
          totalProducts: 145, // Would come from Firestore
          pendingEditorials: 3, // Would come from editorial system
          activeSignals: 12, // Would come from signals system
          pendingUserContent: 8, // Would come from user content system
          totalRestaurants: 116, // Would come from Firebase restaurant collections
          restaurantsWithLiveData: 0, // Would come from Firebase live data check
          // NEW CONTRIBUTOR STATS - Would come from Firestore
          pendingContributors: 5,
          pendingContributorProducts: 23,
          pendingContributorArticles: 7,
          pendingContributorSignals: 3
        });

        // Mock recent activity with contributor data
        setRecentActivity([
          { type: 'contributor', action: 'applied', count: 2, time: '1 hour ago' },
          { type: 'discover', action: 'approved', count: 5, time: '2 hours ago' },
          { type: 'contributor-product', action: 'approved', count: 8, time: '3 hours ago' },
          { type: 'product', action: 'added', count: 3, time: '4 hours ago' },
          { type: 'user-content', action: 'rejected', count: 2, time: '6 hours ago' },
          { type: 'contributor-article', action: 'published', count: 1, time: '1 day ago' },
          { type: 'editorial', action: 'published', count: 1, time: '1 day ago' },
          { type: 'signals', action: 'updated', count: 4, time: '2 days ago' }
        ]);

      } catch (error) {
        console.error('Error loading admin stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadStats();
  }, []);

  // Navigation handler
  const handleNavigation = (path) => {
    navigate(path);
  };

  const adminTools = [
    {
      title: 'Discover Curation',
      description: 'Review and approve content for the discover feed',
      path: '/admin/discover',
      icon: '🔍',
      stats: `${stats.pendingDiscover} pending`,
      color: '#007bff',
      urgent: stats.pendingDiscover > 10
    },
    {
      title: 'Product Curation',
      description: 'Manage shop products and affiliate content',
      path: '/admin/products',
      icon: '🛍️',
      stats: `${stats.totalProducts} total`,
      color: '#28a745',
      urgent: false
    },
    {
      title: 'Guides Curation',
      description: 'Manage restaurant live data and weekly batch processing',
      path: '/admin/guides',
      icon: '🗺️',
      stats: `${stats.totalRestaurants} restaurants`,
      color: '#FF6B35',
      urgent: stats.restaurantsWithLiveData === 0
    },
    {
      title: 'Revenue Tracking',
      description: 'Monitor editorial-to-shop conversions and platform revenue',
      path: '/admin/revenue',
      icon: '💰',
      stats: 'Live data',
      color: '#17a2b8',
      urgent: false
    },
    {
      title: 'Revenue Analytics',
      description: 'Deep analytics on revenue performance and user segments',
      path: '/admin/analytics',
      icon: '📊',
      stats: 'Detailed insights',
      color: '#6f42c1',
      urgent: false
    },
    {
      title: 'Editorial Curation',
      description: 'Manage editorial posts and featured content',
      path: '/admin/editorial',
      icon: '📝',
      stats: `${stats.pendingEditorials} drafts`,
      color: '#6f42c1',
      urgent: stats.pendingEditorials > 5
    },
    {
      title: 'Signals Curation',
      description: 'Manage brand partnerships and signal placements',
      path: '/admin/signals',
      icon: '📡',
      stats: `${stats.activeSignals} active`,
      color: '#fd7e14',
      urgent: false
    },
    {
      title: 'User Content Curation',
      description: 'Review user-submitted images and content',
      path: '/admin/user-content',
      icon: '👥',
      stats: `${stats.pendingUserContent} pending`,
      color: '#dc3545',
      urgent: stats.pendingUserContent > 15
    },
    {
      title: 'User Product Curation',
      description: 'Review user-submitted products and affiliate requests',
      path: '/admin/user-products',
      icon: '📦',
      stats: 'Coming soon',
      color: '#17a2b8',
      urgent: false
    },
    {
      title: 'Affiliate Approval',
      description: 'Manage affiliate program applications and vendor partnerships',
      path: '/admin/affiliates',
      icon: '🤝',
      stats: 'Coming soon',
      color: '#6610f2',
      urgent: false
    },
    // NEW CONTRIBUTOR MANAGEMENT TOOLS
    {
      title: 'Contributor Management',
      description: 'Review contributor applications and manage approved contributors',
      path: '/admin/contributors',
      icon: '🤝',
      stats: `${stats.pendingContributors || 0} pending`,
      color: '#28a745',
      urgent: (stats.pendingContributors || 0) > 5
    },
    {
      title: 'Contributor Products',
      description: 'Review and approve contributor product submissions',
      path: '/admin/contributor-products',
      icon: '📦',
      stats: `${stats.pendingContributorProducts || 0} pending`,
      color: '#007bff',
      urgent: (stats.pendingContributorProducts || 0) > 10
    },
    {
      title: 'Contributor Articles',
      description: 'Review editorial submissions for Apple News publication',
      path: '/admin/contributor-articles',
      icon: '📰',
      stats: `${stats.pendingContributorArticles || 0} pending`,
      color: '#6f42c1',
      urgent: (stats.pendingContributorArticles || 0) > 3
    },
    {
      title: 'Contributor Signals',
      description: 'Review and approve contributor advertising requests',
      path: '/admin/contributor-signals',
      icon: '📡',
      stats: `${stats.pendingContributorSignals || 0} pending`,
      color: '#fd7e14',
      urgent: (stats.pendingContributorSignals || 0) > 5
    }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'discover': return '🔍';
      case 'product': return '🛍️';
      case 'editorial': return '📝';
      case 'signals': return '📡';
      case 'user-content': return '👥';
      case 'guides': return '🗺️';
      case 'contributor': return '🤝';
      case 'contributor-product': return '📦';
      case 'contributor-article': return '📰';
      case 'contributor-signal': return '📡';
      default: return '📊';
    }
  };

  const getActivityColor = (action) => {
    switch (action) {
      case 'approved': case 'published': case 'added': return '#28a745';
      case 'rejected': case 'removed': return '#dc3545';
      case 'updated': case 'modified': return '#ffc107';
      case 'applied': return '#007bff';
      default: return '#6c757d';
    }
  };

  return (
    <div style={{
      maxWidth: '952px',
      margin: '1.36rem auto',
      padding: '1.36rem',
      backgroundColor: 'white',
      borderRadius: '8.16px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '2.04rem' }}>
        <h1 style={{ margin: '0 0 0.34rem 0', color: '#333', fontSize: '1.36rem' }}>
          Admin Dashboard
        </h1>
        <p style={{ margin: 0, color: '#666', fontSize: '0.748rem' }}>
          Manage all aspects of the Artifex platform
        </p>
      </div>

      {/* Quick Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(136px, 1fr))', 
        gap: '0.68rem',
        marginBottom: '2.04rem'
      }}>
        <div style={{
          padding: '1.02rem',
          backgroundColor: '#e3f2fd',
          borderRadius: '5.44px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.36rem', fontWeight: 'bold', color: '#1976d2' }}>
            {stats.pendingDiscover}
          </div>
          <div style={{ fontSize: '0.612rem', color: '#666' }}>Pending Discover</div>
        </div>

        <div style={{
          padding: '1.02rem',
          backgroundColor: '#e8f5e8',
          borderRadius: '5.44px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.36rem', fontWeight: 'bold', color: '#388e3c' }}>
            {stats.totalProducts}
          </div>
          <div style={{ fontSize: '0.612rem', color: '#666' }}>Total Products</div>
        </div>

        <div style={{
          padding: '1.02rem',
          backgroundColor: '#fff3e0',
          borderRadius: '5.44px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.36rem', fontWeight: 'bold', color: '#FF6B35' }}>
            {stats.totalRestaurants}
          </div>
          <div style={{ fontSize: '0.612rem', color: '#666' }}>Total Restaurants</div>
        </div>

        <div style={{
          padding: '1.02rem',
          backgroundColor: '#f3e5f5',
          borderRadius: '5.44px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.36rem', fontWeight: 'bold', color: '#7b1fa2' }}>
            {stats.pendingEditorials}
          </div>
          <div style={{ fontSize: '0.612rem', color: '#666' }}>Draft Editorials</div>
        </div>

        <div style={{
          padding: '1.02rem',
          backgroundColor: '#ffebee',
          borderRadius: '5.44px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.36rem', fontWeight: 'bold', color: '#d32f2f' }}>
            {stats.pendingUserContent}
          </div>
          <div style={{ fontSize: '0.612rem', color: '#666' }}>Pending User Content</div>
        </div>

        {/* NEW CONTRIBUTOR STATS */}
        <div style={{
          padding: '1.02rem',
          backgroundColor: '#e8f5e8',
          borderRadius: '5.44px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.36rem', fontWeight: 'bold', color: '#28a745' }}>
            {stats.pendingContributors}
          </div>
          <div style={{ fontSize: '0.612rem', color: '#666' }}>Pending Contributors</div>
        </div>
      </div>

      {/* Admin Tools Grid */}
      <div style={{ marginBottom: '2.04rem' }}>
        <h2 style={{ margin: '0 0 1.02rem 0', color: '#333' }}>Curation Tools</h2>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(204px, 1fr))', 
          gap: '1.02rem' 
        }}>
          {adminTools.map((tool, index) => (
            <div
              key={index}
              onClick={() => handleNavigation(tool.path)}
              style={{
                padding: '1.36rem',
                backgroundColor: 'white',
                border: tool.urgent ? `1.36px solid ${tool.color}` : '0.68px solid #ddd',
                borderRadius: '8.16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
                boxShadow: tool.urgent ? `0 4px 12px ${tool.color}33` : '0 2px 8px rgba(0,0,0,0.1)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 6px 20px ${tool.color}33`;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = tool.urgent ? `0 4px 12px ${tool.color}33` : '0 2px 8px rgba(0,0,0,0.1)';
              }}
            >
              {tool.urgent && (
                <div style={{
                  position: 'absolute',
                  top: '0.34rem',
                  right: '0.34rem',
                  backgroundColor: tool.color,
                  color: 'white',
                  padding: '0.17rem 0.34rem',
                  borderRadius: '8.16px',
                  fontSize: '0.476rem',
                  fontWeight: 'bold'
                }}>
                  ATTENTION
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.68rem' }}>
                <div style={{ 
                  fontSize: '1.36rem', 
                  marginRight: '0.68rem',
                  width: '34px',
                  textAlign: 'center'
                }}>
                  {tool.icon}
                </div>
                <div>
                  <h3 style={{ 
                    margin: '0 0 0.17rem 0', 
                    color: tool.color,
                    fontSize: '0.816rem'
                  }}>
                    {tool.title}
                  </h3>
                  <div style={{ 
                    fontSize: '0.612rem', 
                    color: '#666',
                    fontWeight: 'bold'
                  }}>
                    {tool.stats}
                  </div>
                </div>
              </div>

              <p style={{ 
                margin: 0, 
                color: '#666', 
                fontSize: '0.612rem',
                lineHeight: '1.4'
              }}>
                {tool.description}
              </p>

              <div style={{
                marginTop: '0.68rem',
                padding: '0.34rem 0.68rem',
                backgroundColor: `${tool.color}15`,
                borderRadius: '4.08px',
                textAlign: 'center',
                color: tool.color,
                fontSize: '0.612rem',
                fontWeight: '600'
              }}>
                Open Tool →
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h2 style={{ margin: '0 0 1.02rem 0', color: '#333' }}>Recent Activity</h2>
        
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '1.36rem', color: '#666' }}>
            Loading activity...
          </div>
        ) : (
          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '5.44px',
            padding: '1.02rem'
          }}>
            {recentActivity.map((activity, index) => (
              <div 
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.68rem 0',
                  borderBottom: index < recentActivity.length - 1 ? '0.68px solid #e9ecef' : 'none'
                }}
              >
                <div style={{ 
                  fontSize: '1.02rem', 
                  marginRight: '0.68rem',
                  width: '27.2px',
                  textAlign: 'center'
                }}>
                  {getActivityIcon(activity.type)}
                </div>
                
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#333' }}>
                    {activity.type.charAt(0).toUpperCase() + activity.type.slice(1).replace('-', ' ')}
                  </div>
                  <div style={{ fontSize: '0.612rem', color: '#666' }}>
                    <span style={{ 
                      color: getActivityColor(activity.action),
                      fontWeight: '600' 
                    }}>
                      {activity.action.charAt(0).toUpperCase() + activity.action.slice(1)}
                    </span>
                    {' '}{activity.count} items
                  </div>
                </div>
                
                <div style={{ 
                  fontSize: '0.544rem', 
                  color: '#999',
                  textAlign: 'right'
                }}>
                  {activity.time}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions Footer */}
      <div style={{
        marginTop: '2.04rem',
        padding: '1.02rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '5.44px',
        textAlign: 'center'
      }}>
        <h3 style={{ margin: '0 0 0.68rem 0', color: '#333' }}>Quick Actions</h3>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.68rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => handleNavigation('/admin/discover')}
            style={{
              padding: '0.51rem 1.02rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4.08px',
              cursor: 'pointer',
              fontSize: '0.612rem'
            }}
          >
            Review Pending Content
          </button>
          
          <button
            onClick={() => handleNavigation('/admin/contributors')}
            style={{
              padding: '0.51rem 1.02rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4.08px',
              cursor: 'pointer',
              fontSize: '0.612rem'
            }}
          >
            Review Contributors
          </button>
          
          <button
            onClick={() => handleNavigation('/admin/guides')}
            style={{
              padding: '0.51rem 1.02rem',
              backgroundColor: '#FF6B35',
              color: 'white',
              border: 'none',
              borderRadius: '4.08px',
              cursor: 'pointer',
              fontSize: '0.612rem'
            }}
          >
            Refresh Restaurant Data
          </button>
          
          <button
            onClick={() => handleNavigation('/admin/products')}
            style={{
              padding: '0.51rem 1.02rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4.08px',
              cursor: 'pointer',
              fontSize: '0.612rem'
            }}
          >
            Add New Products
          </button>
          
          <button
            onClick={() => handleNavigation('/admin/revenue')}
            style={{
              padding: '0.51rem 1.02rem',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4.08px',
              cursor: 'pointer',
              fontSize: '0.612rem'
            }}
          >
            View Revenue Dashboard
          </button>
          
          <button
            onClick={() => handleNavigation('/admin/user-content')}
            style={{
              padding: '0.51rem 1.02rem',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4.08px',
              cursor: 'pointer',
              fontSize: '0.612rem'
            }}
          >
            Moderate User Content
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminNavigation;